/* eslint-disable camelcase */
import { i18n } from '@/i18n';
import { request, CustomException, util } from '@tencent/ppd-base-libs';
import { differenceWith, isEmpty } from 'lodash-es';
import {
  countDowningCouponStore,
  pageConfigStore,
} from '../../business/display-coupon-record';
import { getFormattedDate, getFormattedDateAndTime } from '../../util/dateHelper';
import { Moment } from 'moment';

export type IPosition = 'bottom' | 'scroll';
export enum BARCODE_TYPE {
  code128 = 'code128',
  ean13 = 'ean13'
}
export const BarcodeTypeMap = {
  1: BARCODE_TYPE.code128,
  2: BARCODE_TYPE.ean13,
};
export interface IComponentDataType {
  couponDetail: IDisplayCouponDetail;
  userScid: string;
  currentDate: Moment;
  countDownTime: string;
  countDownInterval: number;
  diffTime: number;
  isShowPage2: boolean;
  isShowCond: boolean;
  isShowCondBtn: boolean;
  miniProgramUrl: string;
  lang?: string;
}
/** 使用优惠券 接口默认为used 无倒计时的券是打开展示券open */
export type ExecCouponUseDoneCmd = 'open' | 'used';
export type ExecCouponUseDoneParams = {
  user_scid: IComponentDataType['userScid'],
  cmd?: ExecCouponUseDoneCmd
};
export enum COUPON_TYPE {
  NO_CDKEY = '5001',
  CDKEY = '5002',
  SINGLE_CDKEY = '5003'
}
export enum CDKEY_SHOW_TYPE {
  CODE = '1', // 券码
  CODE_BARCODE = '2', // 券码+条形码
  BARCODE = '3', // 条形码
  QRCODE = '4', // 二维码
  CODE_QRCODE = '5', // 券码+二维码
  BARCODE_QRCODE = '6', // 条形码+二维码
  CODE_BARCODE_QRCODE = '7', // 券码+条形码+二维码
}
export enum TRANS_STATE_TYPE {
  UNUSED = '1',
  USED = '2',
  // USING = '3', // 出于10分钟倒计时的5001券
  OPENED = '3', // 搜索了一下USING并没有用到 这次后端拓展了枚举 用于记录非倒计时的展示券打开状态
}
export enum COUPON_QRY_TYPE {
  VALID = '1',
  INVALID = '2',
  USING = '3',
}
export enum COUNT_DOWN_TYPE {
  OLD = '0', // 存量数据
  DISABLE = '1', // 不设置倒计时
  ENABLE = '2' // 倒计时10min
}

export enum CUSTOM_BUTTON_TYPE {
  NONE = '1', // 无跳转入口
  CUSTOMIZE = '2', // 自定义跳转入口
  PAYMENT = '3' // 收付款跳转入口
}

export const CARD_HEIGHT = 140;
export const LAYOUT_TOP = 15;
export const INVALID_AREA_MARGIN = 50;
export const initPullUpLoadObj = {
  threshold: 0,
  txt: {
    more: ' ',
    noMore: ' ', // 不需要设置文本
  },
};
export const INVALID_AREA_TEXT = i18n('已用及失效商戶電子優惠券');
// 获取商户展示券列表
export interface IDisplayCouponRecord {
  merLogoUrl: string;
  spname: string;
  couponName: string;
  validTime: string;
  expireTime: string;
  userScid: string;
  couponId: string;
}

export interface IDisplayCouponDetail {
  merLogoUrl: string;
  spname: string;
  couponName: string;
  couponCondition: string;
  couponId: string;
  goodsUrl: string;
  couponType: COUPON_TYPE;
  spCdkeyShow: CDKEY_SHOW_TYPE;
  spCdkey: string;
  useTime: string;
  validTime: string;
  expireTime: string;
  couponRange: string;
  couponTandc: string;
  transState: TRANS_STATE_TYPE;
  businessId: string;
  jumpPackage: string;
  countDownType: COUNT_DOWN_TYPE;
  customBtnType: CUSTOM_BUTTON_TYPE;
  jumpName: string;
  jumpUrl: string;
  barcodeType?: BARCODE_TYPE;
}

export interface IUseCouponResult {
  // spCdkey: string;
  useTime: string;
  businessId: string;
  jumpPackage: string;
}

export interface ISingleCdkeyInfo {
  couponId: string;
  spCdkey: string;
  spCdkeyShow?: CDKEY_SHOW_TYPE | string;
}

const COUPON_LIST_CGI = '/app/sp_coupon/prom_sp_coupon_list.cgi';

const formatDisplayList = (res): IDisplayCouponRecord[] => {
  const { couponList = [] } = res;
  return couponList.map(item => ({
    merLogoUrl: item.mer_logo_url,
    spname: item.spname,
    couponName: item.coupon_name,
    validTime: getFormattedDate(item.valid_time),
    expireTime: getFormattedDate(item.expire_time),
    userScid: item.user_scid,
    couponId: item.coupon_id,
  }));
};

type IGetSingleCdkeyInfoReturnType = ISingleCdkeyInfo | undefined;
// eslint-disable-next-line max-len
const getSingleCdkeyInfo = (couponId: string): IGetSingleCdkeyInfoReturnType => getSingleCdkeyList().find(item => item.couponId === couponId);

const handleSingleCdkeyRecord = (couponDetail: IDisplayCouponDetail) => {
  let returnDetail = { ...couponDetail };
  const singleCdkeyInfo = getSingleCdkeyInfo(couponDetail.couponId);
  if (singleCdkeyInfo) {
    returnDetail = {
      ...returnDetail,
      ...singleCdkeyInfo,
      couponType: COUPON_TYPE.CDKEY,
    } as any;
  }
  return returnDetail;
};

async function getCountDowningCouponList(): Promise<IDisplayCouponRecord[]> {
  const res = await request.post(COUPON_LIST_CGI, {
    page_num: 0,
    page_size: 20,
    qry_type: COUPON_QRY_TYPE.USING,
  });
  if (res) {
    // 目前不需要處理：如果在倒計時接口中查到了單cdkey的券，要把它們剔除（因為他們被認為是5002）
    // 1.如果countdown=0,且前端認為5002,則沒有倒計時，則點擊使用不會調使用接口=>未使用。對於後端，一直是5001且未使用
    // 2.如果countdown=1,且前端認為5002,點擊了使用有倒計時，這時候qry3接口應返回正在倒計時，不用filter掉
    // 3.如果countdown=2,且前端認為5002,則沒有倒計時=>未使用。對於後端，一直是5001且未使用
    return formatDisplayList(res);
  }
  return [];
}

// eslint-disable-next-line camelcase
export async function getDisplayCouponList(): Promise<{
  data: IDisplayCouponRecord[];
  hasMore: boolean;
}> {
  // {page_num: number;page_size: number;qry_type: string}
  const { pageNum, pageSize, qryType } = pageConfigStore.get();
  if (pageNum === 0) {
    countDowningCouponStore.set(await getCountDowningCouponList());
  }
  const res = await request.post(COUPON_LIST_CGI, {
    page_num: pageNum,
    page_size: pageSize,
    qry_type: qryType,
  });
  let resData;
  if (res) {
    const formattedList = formatDisplayList(res);
    const countDowningCouponList = countDowningCouponStore.get();
    const hasMore = formattedList.length === pageSize;
    if (qryType === COUPON_QRY_TYPE.VALID) {
      resData = {
        data:
          !isEmpty(countDowningCouponList) && pageNum === 0
            ? countDowningCouponList.concat(formattedList)
            : formattedList,
        hasMore,
      };
    }
    if (qryType === COUPON_QRY_TYPE.INVALID) {
      const data = differenceWith(
        formattedList,
        countDowningCouponList,
        (first, second) => first.userScid === second.userScid,
      );
      resData = { data, hasMore };
    }
    pageConfigStore.set({ pageNum: pageNum + 1 }); // 页数自增1
    return resData;
  }
  throw new Error(i18n('返回內容為空'));
}

// 兼容商户展示券返回encode的tnc
const tryDecode = (tnc) => {
  let newTNC = tnc;
  try {
    newTNC = decodeURIComponent(tnc);
  } catch (err) { }
  return newTNC;
};

const formatCustomBtnType = (res: any): { customBtnType: CUSTOM_BUTTON_TYPE, jumpName: string, jumpUrl: string } => {
  const {
    customize_jump_name: jumpName,
    customize_jump_url: jumpUrl,
    count_down: countDown,
  } = res;
  if ((jumpName === '前往付款' && jumpUrl === '前往付款') || countDown === COUNT_DOWN_TYPE.OLD) {
    return { customBtnType: CUSTOM_BUTTON_TYPE.PAYMENT, jumpName: '前往付款', jumpUrl: '' };
  }
  if (jumpName === '' && jumpUrl === '') {
    return { customBtnType: CUSTOM_BUTTON_TYPE.NONE, jumpName, jumpUrl };
  }
  return { customBtnType: CUSTOM_BUTTON_TYPE.CUSTOMIZE, jumpName, jumpUrl: tryDecode(jumpUrl) };
};

// 获取商户展示券详情
export async function getDisplayCouponDetail(data): Promise<IDisplayCouponDetail> {
  // 加载单cdkey配置
  await loadSingleCdkeyData();

  const res = await request.post(
    '/app/sp_coupon/prom_sp_coupon_detail.cgi',
    data,
  );

  if (res) {
    const detail = {
      merLogoUrl: res.mer_logo_url,
      spname: res.spname,
      couponName: res.coupon_name,
      couponCondition: res.coupon_condition,
      couponId: res.coupon_id,
      goodsUrl: res.goods_url,
      couponType: res.coupon_type,
      spCdkeyShow: res.sp_cdkey_show,
      spCdkey: res.sp_cdkey,
      useTime: res.use_time, // 不能在这里format
      validTime: getFormattedDate(res.valid_time),
      expireTime: res.expire_time, // 这里不能format成年月日，因为判断过期需要用时分秒
      couponRange: tryDecode(res.coupon_range),
      couponTandc: tryDecode(res.coupon_tandc),
      transState: res.trans_state,
      businessId: res.business_id,
      jumpPackage: res.jump_package,
      countDownType: res.count_down as COUNT_DOWN_TYPE,
      barcodeType: BarcodeTypeMap[res.code_format],
      ...formatCustomBtnType(res),
    };
    // 判断，并进行单cdkey数据单独处理
    return handleSingleCdkeyRecord(detail);
  }
  throw new Error(i18n('返回內容為空'));
}

// 使用商户展示券
export async function useDisplayCoupon(data: ExecCouponUseDoneParams): Promise<IUseCouponResult> {
  const res = await request.post(
    '/app/sp_coupon/prom_sp_coupon_usedone.cgi',
    data,
  );
  if (res) {
    return {
      // spCdkey: res.sp_cdkey, // 界面不需要使用这个值，spCdkey从couponDetail中取
      useTime: res.use_time,
      businessId: res.business_id,
      jumpPackage: res.jump_package,
    };
  }
  throw new Error(i18n('返回內容為空'));
}

// 获取红点信息
export async function getDisplayCouponInfo(data = {}) {
  const res = await request.post(
    '/app/sp_coupon/prom_qry_sp_coupon_cnt.cgi',
    data,
  );
  if (res) {
    return res;
  }
  throw new Error(i18n('獲取商戶展示券紅點失敗'));
}

let cfgData;
export const getMarkUseWhitelist = () => cfgData || [];
export const loadMarkUseWhitelistCfg = async () => {
  if (cfgData) {
    return cfgData;
  }
  const [err, data] = await util.to(request.loadConfig('https://prom.wechatpay.com.hk/fmd_mojo/ppd_config/markUseWhiteList/config.json'));
  if (err) {
    throw new CustomException(err, 'loadCfgFail', '商戶展示券[標記為已使用]白名單加载配置失败');
  }
  const formatRes = () => {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (data && data.configList) {
      return (data.configList || []).map(item => item.couponId);
    }
    return [];
  };
  cfgData = formatRes();
  return cfgData;
};

let singleCdkeyData;
export const getSingleCdkeyList = () => singleCdkeyData || [];
export const loadSingleCdkeyData = async () => {
  if (singleCdkeyData) {
    return singleCdkeyData;
  }
  const [err, data] = await util.to(request.loadConfig('https://prom.wechatpay.com.hk/fmd_mojo/ppd_config/singleCdkeyList/config.json'));
  if (err) {
    throw new CustomException(err, 'loadCfgFail', '商戶展示券[单cdkey配置]加载配置失败');
  }
  singleCdkeyData = data || [];
  return singleCdkeyData;
};


// ref：
// bigBannerImg: "https://st.moneydata.hk/res/mojo_static/2eb5d1cb3815b19da2750af103bae922.png"
// bigBannerLink: "http://qq.com"
// customSubTitle: ""
// endTime: "2021-07-13 00:00:00"
// isOnline: "1"
// smallBannerImg: "https://st.moneydata.hk/res/mojo_static/f00a0ca99aaa6ea473a6a118303dfdeb.png"
// smallBannerLink: "http://qq.com"
// startTime: "2021-07-06 00:00:00"
let bannerConfig;
const getBigBannerLeftText = () => {
  if (bannerConfig.customSubTitle) {
    return bannerConfig.customSubTitle;
  }
  return `${'活動時間'}：${getFormattedDate(bannerConfig.startTime, 'YYYY.MM.DD')}-${getFormattedDate(bannerConfig.endTime, 'YYYY.MM.DD')}`;
};

export const getBannerConfig = () => ({
  smallBannerImg: bannerConfig.smallBannerImg || '',
  smallBannerLink: bannerConfig.smallBannerLink || '',
  bigBannerImg: bannerConfig.bigBannerImg || '',
  bigBannerLink: bannerConfig.bigBannerLink || '',
  bigBannerLeftText: getBigBannerLeftText(),
});

export const loadBannerConfig = async () => {
  if (bannerConfig) {
    return bannerConfig;
  }
  const [err, data = []] = await util.to(request.loadVtools('https://prom.wechatpay.com.hk/fmd_mojo/ppd_config/discountCenterSpCouponBanner/config.json'));
  if (err) {
    // throw new CustomException(err, 'loadCfgFail', '商戶展示券營銷banner加载配置失败');
  }
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (data && data.configList) {
    bannerConfig = data.configList[0] || {};
  };
  return bannerConfig;
};
